import React, { createContext, useContext, useState, useRef } from 'react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface VoiceContextType {
  conversationHistory: Message[];
  addMessage: (message: Message) => void;
  clearHistory: () => void;
  stopAllSpeech: () => void;
  registerSpeechControl: (control: SpeechControl) => void;
  unregisterSpeechControl: (control: SpeechControl) => void;
}

interface SpeechControl {
  stop: () => void;
}

const VoiceContext = createContext<VoiceContextType | undefined>(undefined);

export const VoiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [conversationHistory, setConversationHistory] = useState<Message[]>([]);
  const speechControls = useRef<Set<SpeechControl>>(new Set());

  const addMessage = (message: Message) => {
    setConversationHistory(prev => [...prev, message]);
  };

  const clearHistory = () => {
    setConversationHistory([]);
  };

  const stopAllSpeech = () => {
    // Stop all registered speech controls
    speechControls.current.forEach(control => {
      try {
        control.stop();
      } catch (e) {
        console.error('Error stopping speech:', e);
      }
    });
    
    // Also stop browser TTS
    window.speechSynthesis.cancel();
  };

  const registerSpeechControl = (control: SpeechControl) => {
    speechControls.current.add(control);
  };

  const unregisterSpeechControl = (control: SpeechControl) => {
    speechControls.current.delete(control);
  };

  return (
    <VoiceContext.Provider
      value={{
        conversationHistory,
        addMessage,
        clearHistory,
        stopAllSpeech,
        registerSpeechControl,
        unregisterSpeechControl
      }}
    >
      {children}
    </VoiceContext.Provider>
  );
};

export const useVoice = () => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within VoiceProvider');
  }
  return context;
};
